import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, 
  User, 
  Phone, 
  MapPin, 
  IdCard,
  ClipboardList,
  FileText,
  Pencil,
  Trash2
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientDetailPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [records, setRecords] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClientData();
  }, [clientId]);

  const fetchClientData = async () => {
    try {
      const [clientRes, recordsRes, docsRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}`),
        axios.get(`${API}/support-records?client_id=${clientId}`),
        axios.get(`${API}/documents?client_id=${clientId}`)
      ]);
      setClient(clientRes.data);
      setRecords(recordsRes.data);
      setDocuments(docsRes.data);
    } catch (error) {
      console.error('データの取得に失敗しました', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/clients/${clientId}`);
      navigate('/clients');
    } catch (error) {
      console.error('削除に失敗しました', error);
    }
  };

  const getGenderLabel = (gender) => {
    const labels = { male: '男性', female: '女性', other: 'その他' };
    return labels[gender] || gender;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">利用者が見つかりません</p>
        <Link to="/clients">
          <Button className="mt-4" variant="outline">一覧に戻る</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="client-detail-page">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/clients')}
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">
              {client.name}
            </h1>
            <p className="text-muted-foreground">{client.name_kana}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => navigate(`/clients/${clientId}/edit`)}
            data-testid="edit-client-button"
          >
            <Pencil size={16} className="mr-2" />
            編集
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" data-testid="delete-client-button">
                <Trash2 size={16} className="mr-2" />
                削除
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>利用者を削除しますか？</AlertDialogTitle>
                <AlertDialogDescription>
                  この操作は取り消せません。{client.name}さんのすべてのデータが削除されます。
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>キャンセル</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete}>削除する</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* タブ */}
      <Tabs defaultValue="info" className="space-y-6">
        <TabsList>
          <TabsTrigger value="info" data-testid="tab-info">基本情報</TabsTrigger>
          <TabsTrigger value="records" data-testid="tab-records">支援記録 ({records.length})</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">書類 ({documents.length})</TabsTrigger>
        </TabsList>

        {/* 基本情報タブ */}
        <TabsContent value="info" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* 基本情報カード */}
            <Card className="card-surface">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User size={20} className="text-primary" />
                  基本情報
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="label-text">生年月日</p>
                    <p className="font-medium">{client.birth_date || '未設定'}</p>
                  </div>
                  <div>
                    <p className="label-text">性別</p>
                    <p className="font-medium">{getGenderLabel(client.gender)}</p>
                  </div>
                </div>
                <div>
                  <p className="label-text flex items-center gap-1">
                    <MapPin size={14} />
                    住所
                  </p>
                  <p className="font-medium">{client.address || '未設定'}</p>
                </div>
              </CardContent>
            </Card>

            {/* 障害・負担情報カード */}
            <Card className="card-surface">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <IdCard size={20} className="text-primary" />
                  障害・負担情報
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="label-text">受給者番号</p>
                  <p className="font-medium font-mono">{client.recipient_number || '未設定'}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="label-text">障害種別</p>
                    <p className="font-medium">{client.disability_type || '未設定'}</p>
                  </div>
                  <div>
                    <p className="label-text">障害支援区分</p>
                    <p className="font-medium">{client.disability_grade || '未設定'}</p>
                  </div>
                </div>
                <div>
                  <p className="label-text">利用者負担上限月額</p>
                  <p className="font-medium text-lg">
                    ¥{(client.burden_upper_limit_monthly_amount || 0).toLocaleString()}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* サービス情報カード */}
            <Card className="card-surface">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList size={20} className="text-primary" />
                  利用サービス
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {(client.services || []).map((svc, idx) => (
                    <span key={idx} className="badge-success">
                      {typeof svc === 'string' ? svc : svc.service_type}
                    </span>
                  ))}
                  {(!client.services || client.services.length === 0) && (
                    <p className="text-muted-foreground">未設定</p>
                  )}
                </div>
                {client.notes && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="label-text">備考</p>
                    <p className="text-sm mt-1">{client.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* 支援記録タブ */}
        <TabsContent value="records">
          <Card className="card-surface">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>支援記録</CardTitle>
              <Link to={`/records/new?client_id=${clientId}`}>
                <Button size="sm" data-testid="add-record-button">
                  記録を追加
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {records.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <ClipboardList size={48} className="mx-auto mb-2 opacity-50" />
                  <p>支援記録がありません</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {records.map(record => (
                    <div 
                      key={record.id}
                      className="flex items-center justify-between p-4 rounded-lg bg-secondary/50"
                    >
                      <div>
                        <p className="font-medium">{record.service_date}</p>
                        <p className="text-sm text-muted-foreground">
                          {record.service_type} | {record.start_time} - {record.end_time}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{record.duration_minutes}分</p>
                        <p className="text-sm text-muted-foreground">{record.staff_name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 書類タブ */}
        <TabsContent value="documents">
          <Card className="card-surface">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>書類</CardTitle>
              <Link to={`/documents?client_id=${clientId}`}>
                <Button size="sm" data-testid="upload-document-button">
                  書類をアップロード
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {documents.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText size={48} className="mx-auto mb-2 opacity-50" />
                  <p>書類がありません</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {documents.map(doc => (
                    <div 
                      key={doc.id}
                      className="flex items-center justify-between p-4 rounded-lg bg-secondary/50"
                    >
                      <div className="flex items-center gap-3">
                        <FileText size={24} className="text-primary" />
                        <div>
                          <p className="font-medium">{doc.file_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {doc.document_type || '種別未設定'}
                          </p>
                        </div>
                      </div>
                      <span className={
                        doc.ocr_status === 'completed' ? 'badge-success' :
                        doc.ocr_status === 'processing' ? 'badge-warning' :
                        doc.ocr_status === 'failed' ? 'badge-error' : 'badge-neutral'
                      }>
                        {doc.ocr_status === 'completed' ? 'OCR完了' :
                         doc.ocr_status === 'processing' ? '処理中' :
                         doc.ocr_status === 'failed' ? '失敗' : '未処理'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
